import { memo } from 'react'
import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface AddPanelButtonProps {
  /** Index at which a new panel would be inserted */
  insertIndex: number
  /** Called when the button is clicked, with the insert index */
  onClick: (insertIndex: number) => void
}

const AddPanelButton = memo(function AddPanelButton({ insertIndex, onClick }: AddPanelButtonProps) {
  const { t } = useTranslation('canvas')
  return (
    <button
      className="nodrag nopan"
      onClick={(e) => {
        e.stopPropagation()
        onClick(insertIndex)
      }}
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#FFF',
        border: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
      aria-label={t('addPanelButton.addPanelAtPosition', { position: insertIndex + 1 })}
    >
      <Plus size={12} color="#000" strokeWidth={2} />
    </button>
  )
})

AddPanelButton.displayName = 'AddPanelButton'
export { AddPanelButton }

interface AddPlaceholderSlotProps {
  width: number
  height: number
  insertIndex: number
  onClick: (insertIndex: number) => void
  onRemove?: () => void
  highlighted?: boolean
}

const AddPlaceholderSlot = memo(function AddPlaceholderSlot({
  width,
  height,
  insertIndex,
  onClick,
  onRemove,
  highlighted,
}: AddPlaceholderSlotProps) {
  const { t } = useTranslation('canvas')
  return (
    <button
      className="nodrag nopan"
      onClick={(e) => {
        e.stopPropagation()
        onClick(insertIndex)
      }}
      style={{
        width,
        height,
        borderRadius: 8,
        border: highlighted
          ? '2px solid #00C853'
          : '2px dashed rgba(255,255,255,0.2)',
        background: highlighted
          ? 'rgba(0, 200, 83, 0.08)'
          : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        position: 'relative',
        transition: 'border-color 0.15s ease, background 0.15s ease',
      }}
      aria-label={onRemove ? t('addPanelButton.emptySlotAtPosition', { position: insertIndex + 1 }) : t('addPanelButton.addPanelAtEnd')}
    >
      <Plus size={24} color="rgba(255,255,255,0.4)" strokeWidth={2} />
      {onRemove && (
        <div
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'rgba(220, 38, 38, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          role="button"
          aria-label={t('addPanelButton.removeEmptySlot', { position: insertIndex + 1 })}
        >
          <X size={12} color="#fff" strokeWidth={2} />
        </div>
      )}
    </button>
  )
})

AddPlaceholderSlot.displayName = 'AddPlaceholderSlot'
export { AddPlaceholderSlot }
